import {css, view} from '@sail/ui/deprecated';
import {css as cssStable, view as viewStable} from '@sail/ui';
import {StripeAssistantWidgetHeader} from '../ChatNext/StripeAssistantWidgetHeader';
import {Wave} from '../ChatNext/Wave';

import {useWidgetVisibility} from './useWidget';
import {isIframed} from './iframe';
import {useTheme} from '../ThemeProvider';

type PaneProps = {
  children: React.ReactNode;
};

function Pane({children}: PaneProps) {
  const {visibility} = useWidgetVisibility();

  if (visibility === 'closed') {
    return null;
  }

  const styles = [
    css({
      position: css.value('relative'),
      flexGrow: css.value('1'),
      stack: 'y',
      overflow: 'hidden',
    }),
  ];

  if (visibility !== 'open') {
    styles.push(
      css({
        height: css.value('0'),
        visibility: css.value('hidden'),
      }),
    );
  }

  return (
    <view.div data-testid="conversations-widget-pane" uses={styles}>
      {children}
    </view.div>
  );
}

const universalStyles = cssStable({
  ':global body': {
    // Since we iframe the widget we must ensure there's no body margin or it'll
    // create a weird gap inside the iframe.
    margin: 0,
  },

  // These apply the default font styles for the entire app
  color: 'text',
  font: 'body.small',
});

const iframeStyles = cssStable({
  ':global html': {
    height: '100vh',
    overflow: 'hidden',
    position: 'relative',
  },
});

type Props = {
  header?: React.ReactNode;
  children: React.ReactNode;
};

export function WidgetNext({
  header = <StripeAssistantWidgetHeader />,
  children,
}: Props) {
  const styles = [universalStyles];
  const theme = useTheme();
  if (isIframed()) {
    styles.push(iframeStyles);
  }

  return (
    <viewStable.div
      data-testid="conversations-widget-root"
      onMouseEnter={() => {
        // TODO: this is a work around for the Tooltip to show up immediately
        // It helps the modality as "pointer" (as oppose to "keyboard"), and tooltip is only shown if the user is using pointer
        document.dispatchEvent(new MouseEvent('pointerdown'));
        document.dispatchEvent(new MouseEvent('mousedown'));
      }}
      css={{
        overflow: 'hidden',
        position: 'relative',
        stack: 'y',
        height: 'fill',
        backgroundColor: 'white',
      }}
      uses={styles}
    >
      {header}
      <viewStable.div
        css={{
          height: 'fill',
          overflow: 'clip',
          position: 'relative',
          stack: 'y',
          ':before': {
            content: '""',
            position: 'absolute',
            top: '-80px',
            left: 'space.0',
            right: 'space.0',
            height: '430px',
            width: 'fill',
            backgroundSize: 'cover',
          },
        }}
      >
        <Wave
          css={{
            position: 'absolute',
            width: 'fill',
            top: '0px',
          }}
          focused={false}
          loading={false}
          audience={theme.audience || 'stripe'}
        />

        <Pane>{children}</Pane>
      </viewStable.div>
    </viewStable.div>
  );
}
